import { CONVERSATION_CAPABILITY_CONTRACTS, type ConversationCapabilityKey } from '@/features/conversation-capabilities/contract';
import { userApi, type OnboardingCreationModule } from '@/lib/api/users';
import { getOnboardingPreference } from '@/lib/onboarding';
import { useAuthStore } from '@/stores/authStore';
import { useUserProfileStore } from '@/stores/userProfileStore';

// This receipt belongs to an explicit guide launch, never to hydrated task history.
const STORAGE_KEY = 'dobidobi:onboarding-creation-receipt:v1';
const RECEIPT_TTL_MS = 24 * 60 * 60 * 1000;
interface CreationReceipt {
  id: string;
  userId: string;
  module: OnboardingCreationModule;
  capability: ConversationCapabilityKey;
  createdAt: number;
  started: boolean;
  requests: string[];
  tasks: string[];
  rejectedTasks: string[];
}
let receipt: CreationReceipt | null = null;
const startPromises = new Map<string, Promise<void>>();
const completionPromises = new Map<string, Promise<void>>();
// Preserve activation write order when the user starts another guide while a receipt is in flight.
let mutationQueue: Promise<unknown> = Promise.resolve();
function enqueueActivationMutation(action: () => Promise<void>): Promise<void> {
  const next = mutationQueue.then(action, action);
  mutationQueue = next.catch(() => undefined);
  return next;
}

function saveReceipt(): void {
  if (typeof window === 'undefined') return;
  try {
    if (receipt) window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(receipt));
    else window.sessionStorage.removeItem(STORAGE_KEY);
  } catch { /* Memory survives SPA navigation when storage is unavailable. */ }
}

function currentReceipt(): CreationReceipt | null {
  if (!receipt && typeof window !== 'undefined') {
    try {
      const value = JSON.parse(window.sessionStorage.getItem(STORAGE_KEY) || 'null') as CreationReceipt | null;
      if (value && typeof value.id === 'string' && typeof value.userId === 'string'
        && value.capability in CONVERSATION_CAPABILITY_CONTRACTS
        && ['product', 'people', 'design', 'ugc'].includes(value.module)
        && typeof value.createdAt === 'number' && Array.isArray(value.requests) && Array.isArray(value.tasks) && Array.isArray(value.rejectedTasks)) receipt = value;
    } catch { /* A missing or malformed receipt cannot authorize completion. */ }
  }
  if (!receipt || receipt.userId !== useAuthStore.getState().user?.id) return null;
  if (Date.now() - receipt.createdAt > RECEIPT_TTL_MS || receipt.createdAt > Date.now()) return null;
  return receipt;
}

/** Persist started independently of the guide, which can unmount on navigation. */
export function startOnboardingCreation(module: OnboardingCreationModule, capability: ConversationCapabilityKey): Promise<void> {
  const userId = useAuthStore.getState().user?.id;
  const profile = useUserProfileStore.getState().profile;
  if (!userId || profile?.user_id !== userId || getOnboardingPreference(profile)?.activation?.status === 'completed') return Promise.resolve();
  const launch: CreationReceipt = {
    id: crypto.randomUUID(), userId, module, capability, createdAt: Date.now(),
    started: false, requests: [], tasks: [], rejectedTasks: [],
  };
  receipt = launch;
  saveReceipt();
  const started = enqueueActivationMutation(async () => {
    if (currentReceipt()?.id !== launch.id) return;
    const response = await userApi.updateOnboardingActivation({ status: 'started', module });
    if (response.error || !response.data || response.data.user_id !== launch.userId) throw new Error('activation_update_failed');
    if (currentReceipt()?.id !== launch.id) return;
    useUserProfileStore.getState().applyServerProfile(response.data);
    launch.started = true;
    saveReceipt();
  });
  startPromises.set(launch.id, started);
  void started.then(() => startPromises.delete(launch.id), () => startPromises.delete(launch.id));
  return started;
}

/** Only a newly submitted matching capability request may claim this launch. */
export function registerOnboardingCreationRequest(requestId: string, skillId: string | null | undefined): void {
  const launch = currentReceipt();
  if (!launch) return;
  const execution = CONVERSATION_CAPABILITY_CONTRACTS[launch.capability].execution;
  if (!('skillId' in execution) || execution.skillId !== skillId) return;
  if (!launch.requests.includes(requestId)) launch.requests.push(requestId);
  saveReceipt();
}

export function bindOnboardingCreationTask(requestId: string, taskId: string): void {
  const launch = currentReceipt();
  if (!launch?.requests.includes(requestId) || !taskId || launch.rejectedTasks.includes(taskId)) return;
  if (!launch.tasks.includes(taskId)) launch.tasks.push(taskId);
  saveReceipt();
}

/** Capture before awaiting createDeck, so a different launch cannot claim its result. */
export function captureOnboardingDeckLaunch(): string | null {
  const launch = currentReceipt();
  return launch?.capability === 'productDetail' ? launch.id : null;
}
export function bindOnboardingCreationDeck(launchId: string | null, deckId: string): void {
  const launch = currentReceipt();
  if (!launchId || launch?.id !== launchId || !deckId || launch.rejectedTasks.includes(`deck:${deckId}`)) return;
  if (!launch.tasks.includes(`deck:${deckId}`)) launch.tasks.push(`deck:${deckId}`);
  saveReceipt();
}

export function completeOnboardingCreationTask(taskId: string, update: {
  status: string;
  previewUrl?: string;
  outputAssetId?: string;
  error?: string;
}): Promise<void> {
  const launch = currentReceipt();
  if (!launch?.tasks.includes(taskId)) return Promise.resolve();
  if (update.status === 'failed' || update.status === 'cancelled') {
    launch.tasks = launch.tasks.filter((id) => id !== taskId);
    launch.rejectedTasks.push(taskId);
    saveReceipt();
    return Promise.resolve();
  }
  if (update.status !== 'done' || update.error || !(update.previewUrl || update.outputAssetId)) return Promise.resolve();
  const pendingCompletion = completionPromises.get(launch.id);
  if (pendingCompletion) return pendingCompletion;
  const completion = (async () => {
    try {
      await startPromises.get(launch.id);
      if (currentReceipt()?.id !== launch.id || !launch.started || !launch.tasks.includes(taskId)) return;
      let profile = useUserProfileStore.getState().profile;
      if (profile?.user_id !== launch.userId) {
        profile = await useUserProfileStore.getState().fetchProfile(true);
        if (currentReceipt()?.id !== launch.id) return;
      }
      const activation = profile?.user_id === launch.userId ? getOnboardingPreference(profile)?.activation : null;
      if (activation?.status !== 'started' || activation.selected_module !== launch.module) return;
      await enqueueActivationMutation(async () => {
        if (currentReceipt()?.id !== launch.id || !launch.tasks.includes(taskId)) return;
        const latestProfile = useUserProfileStore.getState().profile;
        const latestActivation = latestProfile?.user_id === launch.userId ? getOnboardingPreference(latestProfile)?.activation : null;
        if (latestActivation?.status !== 'started' || latestActivation.selected_module !== launch.module) return;
        const response = await userApi.updateOnboardingActivation({ status: 'completed', module: launch.module });
        if (response.error || !response.data || response.data.user_id !== launch.userId) return;
        // Keep the receipt on failure, allowing an authoritative replay to retry.
        if (currentReceipt()?.id !== launch.id) return;
        useUserProfileStore.getState().applyServerProfile(response.data);
        receipt = null;
        saveReceipt();
      });
    } catch { /* Failed persistence never marks activation completed locally. */ }
    finally { completionPromises.delete(launch.id); startPromises.delete(launch.id); }
  })();
  completionPromises.set(launch.id, completion);
  return completion;
}
